/**
 * Component that renders a single toast notification.
 * Meant to be rendered via renderToast().
 */

import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import Log, {Errors} from "@khan/logging";
import {pageWidth, zindexAboveModal} from "@khan/shared-styles";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import deprecatedReactRender from "#rendering/deprecated-react-render.tsx";

import ArchiveAssignmentNotification from "./toast-notifications/archive-assignment-notification.tsx";
import AssignmentCompletedNotification from "./toast-notifications/assignment-completed-notification.tsx";
import ChangesSavedNotification from "./toast-notifications/changes-saved-notification.tsx";
import CreateAssignmentNotification from "./toast-notifications/create-assignment-notification.tsx";
import CreateSubjectMasteryAssignmentNotification from "./toast-notifications/create-subject-mastery-assignment-notification.tsx";
import DistrictReportLoadingNotification from "./toast-notifications/district-report-loading-notification.tsx";
import ErrorNotification from "./toast-notifications/error-notification.tsx";
import InfoNotification from "./toast-notifications/info-notification.tsx";
import MasteryGoalCreatedNotification from "./toast-notifications/mastery-goal-created-notification.tsx";
import StripeUpdateSuccessNotification from "./toast-notifications/stripe-update-success-notification.tsx";
import UpdateAssignmentNotification from "./toast-notifications/update-assignment-notification.tsx";

const classToComponentMap = {
    ArchiveAssignmentNotification,
    AssignmentCompletedNotification,
    ChangesSavedNotification,
    CreateAssignmentNotification,
    CreateSubjectMasteryAssignmentNotification,
    DistrictReportLoadingNotification,
    UpdateAssignmentNotification,
    ErrorNotification,
    InfoNotification,
    StripeUpdateSuccessNotification,
    MasteryGoalCreatedNotification,
} as const;

type ClassToComponentMap = typeof classToComponentMap;

type ToastNotificationProps = {
    children: (
        components: ClassToComponentMap,
    ) => React.ReactElement<ClassToComponentMap[keyof ClassToComponentMap]>;
    position?: "top right" | "bottom center";
    testId?: string;
};

export default function ToastNotification({
    children,
    position = "top right",
    testId,
}: ToastNotificationProps) {
    return (
        <div
            className={css(
                styles.toast,
                position === "top right"
                    ? styles.topRightToast
                    : styles.bottomCenterToast,
            )}
        >
            <View testId={testId}>{children(classToComponentMap)}</View>
        </div>
    );
}

// These styles could live in BaseToastNotification, but it would make
// it harder to test individual toast notifications via fixtures.
const styles = StyleSheet.create({
    toast: {
        // Toast notifications should always be visible
        position: "fixed",
        zIndex: zindexAboveModal,
    },
    topRightToast: {
        top: 57,
        right: 0,

        // Keep toast notifications aligned to the right edge of the page
        [`@media screen and (min-width: ${pageWidth}px)`]: {
            right: `calc((100% - ${pageWidth}px) / 2)`,
        },
    },
    bottomCenterToast: {
        bottom: spacing.medium_16,
        left: "50%",
        transform: "translate(-50%)",
    },
});

export function renderErrorToast(
    errorText: string,
    {testId}: {testId?: string} = {},
) {
    renderToast(
        ({ErrorNotification}) => <ErrorNotification content={errorText} />,
        {position: "bottom center", testId},
    );
}

export function renderInfoToast(
    content: React.ReactNode,
    {testId}: {testId?: string} = {},
) {
    renderToast(
        ({InfoNotification}) => <InfoNotification content={content} />,
        {position: "bottom center", testId},
    );
}

export function renderChangesSavedToast({
    message,
    testId,
}: {message?: string; testId?: string} = {}) {
    renderToast(
        ({ChangesSavedNotification}) => (
            <ChangesSavedNotification message={message} />
        ),
        {position: "bottom center", testId},
    );
}

let container;

export function renderToast(
    render: ToastNotificationProps["children"],
    options?: {position?: "top right" | "bottom center"; testId?: string},
) {
    if (!container) {
        container = document.createElement("div");
        document.body?.appendChild(container);
    }

    // TODO(erik): We should create new component for this in WB:
    // https://khanacademy.atlassian.net/browse/WB-663
    try {
        // Clear the container then re-render ToastNotification
        // TODO(FEI-3875) - move the toast to the app shell
        // This should be using React Portals
        deprecatedReactRender(<React.Fragment />, container, () => {
            deprecatedReactRender(
                <ToastNotification {...options}>{render}</ToastNotification>,
                container,
            );
        });
    } catch (e: any) {
        // NOTE (josh): For unclear reasons, errors have been known to be
        // silently swallowed. Adding a `Log.error` for help during dev/prod.
        Log.error("Error during renderToast", Errors.Internal, {cause: e});
    }
}

type APIToastNotificationProps = {
    // This notification's Python class hierarchy,
    // from most general to most specific.
    class_: Array<string>;
    // Some toasts may have an error variant.
    // If so, it can be passed through here
    isError?: boolean;
    // Position of the toast on the screen. Defaults to "top right."
    position?: "top right" | "bottom center";
    content?: React.ReactNode;
};

export function renderToastFromAPI(props: APIToastNotificationProps) {
    // Find the most specific class that we know how to render
    const classes = props.class_.slice().reverse();

    const renderableClasses = classes.filter((c) =>
        classToComponentMap.hasOwnProperty(c),
    );

    if (!renderableClasses.length) {
        return;
    }

    const Notification = classToComponentMap[renderableClasses[0]];

    renderToast(() => <Notification {...props} />, {
        position: props.position,
        testId: `toast-${renderableClasses[0]}`,
    });
}
